/**
 * Render the embed of a Bluesky feed post. Handles:
 *   - app.bsky.embed.images (1–4 thumbnails, click to open full)
 *   - app.bsky.embed.video (HTML <video> element with the playlist URL)
 *   - app.bsky.embed.external (link card)
 *   - app.bsky.embed.record (quoted post)
 *   - app.bsky.embed.recordWithMedia (above two combined)
 *
 * The shape we receive is the AppView "view" form, not the raw record, so
 * URLs are pre-resolved (post.embed.images[].thumb / fullsize).
 *
 * Images are served by the Service Worker from local Cache API on repeat
 * views — this component only needs to point at the CDN URL.
 */
import { useState } from 'react'
import { HlsVideo } from './HlsVideo'

export interface PostEmbedView {
  $type?: string
  // images
  images?: Array<{
    thumb?: string
    fullsize?: string
    alt?: string
    aspectRatio?: { width: number; height: number }
  }>
  // video (AppView gives a playlist + thumbnail)
  playlist?: string
  thumbnail?: string
  alt?: string
  aspectRatio?: { width: number; height: number }
  // external link card
  external?: {
    uri?: string
    title?: string
    description?: string
    thumb?: string
  }
  // quoted post
  record?: {
    $type?: string
    uri?: string
    cid?: string
    author?: { did?: string; handle?: string; displayName?: string; avatar?: string }
    value?: { text?: string }
    record?: { text?: string }
    embeds?: PostEmbedView[]
  }
  // recordWithMedia: media key holds the inner media embed
  media?: PostEmbedView
}

export function PostMedia({ embed }: { embed?: PostEmbedView }) {
  if (!embed) return null
  const t = embed.$type ?? ''

  if (t.endsWith('embed.images#view') || (Array.isArray(embed.images) && embed.images.length)) {
    return <ImageGrid images={embed.images ?? []} />
  }

  if (t.endsWith('embed.video#view') || embed.playlist) {
    return <VideoBlock embed={embed} />
  }

  if (t.endsWith('embed.external#view') && embed.external) {
    return <ExternalCard ext={embed.external} />
  }

  if (t.endsWith('embed.record#view') && embed.record) {
    return <QuotedPost rec={embed.record} />
  }

  if (t.endsWith('embed.recordWithMedia#view')) {
    return (
      <div className="space-y-1.5">
        {embed.media ? <PostMedia embed={embed.media} /> : null}
        {embed.record ? <QuotedPost rec={embed.record} /> : null}
      </div>
    )
  }

  return null
}

function aspectPadding(ar?: { width: number; height: number }): string {
  if (!ar || ar.width <= 0) return ''
  const pct = Math.min((ar.height / ar.width) * 100, 120) // cap at 120% tall
  return `${pct.toFixed(2)}%`
}

function ImageGrid({
  images,
}: {
  images: NonNullable<PostEmbedView['images']>
}) {
  if (images.length === 0) return null
  const cols =
    images.length === 1
      ? 'grid-cols-1'
      : images.length === 2
        ? 'grid-cols-2'
        : images.length === 3
          ? 'grid-cols-3'
          : 'grid-cols-2'

  return (
    <div className={`mt-1.5 grid gap-0.5 ${cols}`}>
      {images.map((im, i) => (
        <CachedImage
          key={i}
          src={im.thumb ?? im.fullsize}
          fullsize={im.fullsize ?? im.thumb}
          alt={im.alt ?? ''}
          ar={im.aspectRatio}
          priority={i === 0}
        />
      ))}
    </div>
  )
}

function CachedImage({
  src,
  fullsize,
  alt,
  ar,
  priority = false,
}: {
  src?: string
  fullsize?: string
  alt: string
  ar?: { width: number; height: number }
  priority?: boolean
}) {
  const [loaded, setLoaded] = useState(false)
  const pad = aspectPadding(ar)

  return (
    <a
      href={fullsize ?? src}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden border border-white/5 bg-surface-inset"
      title={alt}
    >
      <div className={pad ? 'relative w-full' : 'w-full'} style={pad ? { paddingBottom: pad } : {}}>
        {!loaded && (
          <div
            className={`${pad ? 'absolute inset-0' : 'h-32 w-full'} img-shimmer`}
            aria-hidden
          />
        )}
        <img
          src={src}
          alt={alt}
          className={`${pad ? 'absolute inset-0' : ''} h-full w-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          referrerPolicy="no-referrer"
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'low'}
          onLoad={() => setLoaded(true)}
        />
      </div>
    </a>
  )
}

function VideoBlock({ embed }: { embed: PostEmbedView }) {
  const ar = embed.aspectRatio
  return (
    <div className="mt-1.5 overflow-hidden border border-white/5 bg-black/60">
      {embed.playlist ? (
        ar && ar.width > 0 && ar.height > 0 ? (
          // Force the box to the declared aspect ratio and FILL it, so a
          // stream with a bogus baked-in pixel aspect ratio (SAR) is
          // stretched back to the correct shape. A definite-size wrapper is
          // required — aspect-ratio on the <video> itself is ignored because
          // the element's intrinsic (SAR-distorted) ratio wins.
          <div
            className="relative mx-auto overflow-hidden"
            style={{
              width: `min(100%, calc(480px * ${ar.width} / ${ar.height}))`,
              aspectRatio: `${ar.width} / ${ar.height}`,
            }}
          >
            <HlsVideo
              src={embed.playlist}
              poster={embed.thumbnail}
              controls
              lazy
              className="absolute inset-0 h-full w-full object-fill"
            />
          </div>
        ) : (
          <HlsVideo
            src={embed.playlist}
            poster={embed.thumbnail}
            controls
            lazy
            className="max-h-[480px] w-full object-contain"
          />
        )
      ) : embed.thumbnail ? (
        <img
          src={embed.thumbnail}
          alt={embed.alt ?? ''}
          className="max-h-[480px] w-full object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
          decoding="async"
        />
      ) : null}
    </div>
  )
}

function ExternalCard({
  ext,
}: {
  ext: NonNullable<PostEmbedView['external']>
}) {
  return (
    <a
      href={ext.uri}
      target="_blank"
      rel="noreferrer"
      className="mt-1.5 flex items-stretch gap-2 overflow-hidden border border-white/5 bg-white/5 hover:bg-white/10"
    >
      {ext.thumb ? (
        <img
          src={ext.thumb}
          alt=""
          className="h-20 w-20 shrink-0 object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
          decoding="async"
        />
      ) : null}
      <div className="min-w-0 flex-1 p-2">
        <div className="truncate text-xs font-semibold text-white">
          {ext.title ?? ext.uri}
        </div>
        {ext.description ? (
          <div className="line-clamp-2 text-[11px] text-ink-300">{ext.description}</div>
        ) : null}
        <div className="mt-0.5 truncate text-[10px] text-ink-500">{ext.uri}</div>
      </div>
    </a>
  )
}

function QuotedPost({ rec }: { rec: NonNullable<PostEmbedView['record']> }) {
  const text = rec.value?.text ?? rec.record?.text ?? ''
  return (
    <div className="mt-1.5 border border-white/5 bg-black/30 p-2 text-xs">
      <div className="mb-1 flex items-center gap-1.5 text-[11px]">
        {rec.author?.avatar ? (
          <img
            src={rec.author.avatar}
            alt=""
            className="h-4 w-4 object-cover"
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
          />
        ) : null}
        <span className="font-semibold text-white">
          {rec.author?.displayName ?? '@' + (rec.author?.handle ?? '?')}
        </span>
        <span className="text-ink-400">@{rec.author?.handle}</span>
      </div>
      <p className="whitespace-pre-wrap text-ink-100">{text}</p>
      {rec.embeds?.[0] ? <PostMedia embed={rec.embeds[0]} /> : null}
    </div>
  )
}
